import React, { useState } from 'react';
import { Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Text, Checkbox, Stack, Box, Flex, Input, FormControl, FormLabel, FormErrorMessage, Image, VStack } from '@chakra-ui/react';
import { AxiosError } from 'axios';
import { useUserStore } from '../store/userStore';
import { signOut } from '../utils/signOut';
import apiClient from '../lib/api/apiClient';
import Terms from './Terms';
import { useTerms } from '../hooks/useTerms';
import dominus_logo_transparent_white from '../assets/dominus_logo_transparent_white.png';
import { useUser } from '../hooks/useUser';
import { useModernToast } from './ModernToast';

// Helper functions for phone number formatting
const normalizePhoneNumber = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  // If there are more than 10 digits (e.g., +1 prefix), take the last 10
  return digits.slice(-10);
};

const formatPhoneNumber = (value: string): string => {
  const normalized = normalizePhoneNumber(value);
  if (normalized.length === 0) return '';
  if (normalized.length <= 3) return `(${normalized}`;
  if (normalized.length <= 6) return `(${normalized.slice(0, 3)}) ${normalized.slice(3)}`;
  return `(${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6, 10)}`;
};

const AcceptTerms: React.FC = () => {
  const { user, setUser } = useUserStore();
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [showName, setShowName] = useState(user?.show_name || false);
  const [username, setUsername] = useState(
    user?.username === user?.email ? '' : user?.username || ''
  );
  // Normalize initial phone number if user already has one (strip +1 and formatting)
  const [phoneNumber, setPhoneNumber] = useState(user?.phone ? normalizePhoneNumber(user.phone) : '');
  const [smsConsent, setSmsConsent] = useState(false);
  const [touched, setTouched] = useState<{firstName: boolean, lastName: boolean, username: boolean, phoneNumber: boolean}>({firstName: false, lastName: false, username: false, phoneNumber: false});
  const {
    isOpen: termsOpen,
    documentPath,
    title,
    displayMode,
    showAcceptDecline,
    acceptText,
    declineText,
    onAccept,
    onDecline,
    openTerms,
    closeTerms,
  } = useTerms();
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const { refetch } = useUser();
  const toast = useModernToast();
  // Compute display name for modal logic
  const displayNameValue = showName
    ? [firstName, lastName].filter(Boolean).join(' ').trim() || username
    : username;

  // Show modal if terms not accepted or displayName === email
  const shouldShow =
    !!user &&
    (!user.terms_accepted || displayNameValue === '' || displayNameValue === user.email);


  // Validation
  const firstNameError = touched.firstName && !firstName.trim();
  const lastNameError = touched.lastName && !lastName.trim();
  const usernameError = !showName && touched.username && !username.trim();
  
  // Phone number validation - check for exactly 10 digits
  const normalizedPhone = normalizePhoneNumber(phoneNumber);
  const phoneNumberError = touched.phoneNumber && normalizedPhone.length !== 10;
  const isValidPhoneNumber = normalizedPhone.length === 10;
  
  const isFormValid =
    !!firstName.trim() &&
    !!lastName.trim() &&
    (showName || !!username.trim()) &&
    isValidPhoneNumber &&
    accepted;

  const handleAccept = async () => {
    if (!user) return;
    setSubmitting(true);
    const now = new Date().toISOString();
    const safeUsername = username || "";
    const safeShowName = typeof showName === "boolean" ? showName : false;
    try {
      await apiClient.post('/user/landing', {
        user_id: user.auth_id,
        email: user.email,
      });
      // Normalize phone number to exactly 10 digits before sending (strip all formatting)
      // This ensures we always send digits only, regardless of how the user typed it
      const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);
      
      // Double-check: ensure we only send digits (defensive programming)
      if (normalizedPhoneNumber.length !== 10) {
        throw new Error('Phone number must be exactly 10 digits');
      }
      
      await apiClient.put(`/user/profile`, {
          first_name: firstName,
          last_name: lastName,
          show_name: safeShowName,
          username: safeUsername,
          phone: normalizedPhoneNumber, // Always 10 digits, no formatting
          sms_consent: smsConsent,
          terms_accepted: true,
          terms_date: now
      });
      setUser({ ...user, first_name: firstName, last_name: lastName, show_name: safeShowName, username: safeUsername, phone: normalizedPhoneNumber, sms_consent: smsConsent, terms_accepted: true, terms_date: now });
      const invite = localStorage.getItem('pendingInvite');
      if (invite) {
        await apiClient.post(`/team/accept-invitation/${invite}`);
        refetch();
      }
      localStorage.removeItem('pendingInvite');
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const description =
        axiosError.response?.data?.message ||
        axiosError.message ||
        'We could not save your profile details. Please try again.';
      toast.error('Unable to update your profile', description);
      console.error('Failed to accept terms', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignOut = () => signOut();

  return (
    <Modal isOpen={shouldShow} onClose={() => {}} isCentered closeOnOverlayClick={false} closeOnEsc={false}>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent 
        bg="gray.800" 
        color="white" 
        border="1px solid" 
        borderColor="gray.600"
        maxW="600px"
        w="90%"
      >
        <ModalHeader>
          <VStack spacing={2} align="center">
            <Image src={dominus_logo_transparent_white} alt="Dominus" h={8} />
            <Text fontSize="lg" fontWeight="bold">Complete your Registration</Text>
            <Text fontSize="sm" fontStyle="italic" color="gray.300">
              Dominus is a Mobile Global Esports product
            </Text>
          </VStack>
        </ModalHeader>
        <ModalBody>
          <Stack spacing={4} alignItems="flex-start">
            <Text>To continue, please complete your registration now.</Text>
            <Flex w="100%" gap={4}>
              <FormControl isRequired isInvalid={!!firstNameError}>
                <FormLabel mb={1}>First Name</FormLabel>
                <Input
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  onBlur={() => setTouched(t => ({...t, firstName: true}))}
                  autoComplete="given-name"
                />
                {firstNameError && <FormErrorMessage>First name is required.</FormErrorMessage>}
              </FormControl>
              <FormControl isRequired isInvalid={!!lastNameError}>
                <FormLabel mb={1}>Last Name</FormLabel>
                <Input
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                  onBlur={() => setTouched(t => ({...t, lastName: true}))}
                  autoComplete="family-name"
                />
                {lastNameError && <FormErrorMessage>Last name is required.</FormErrorMessage>}
              </FormControl>
            </Flex>
            <Checkbox
              isChecked={showName}
              onChange={e => setShowName(e.target.checked)}
              colorScheme="green"
              sx={{
                '& .chakra-checkbox__control': {
                  borderColor: '#22a000',
                  _checked: {
                    bg: '#22a000',
                    borderColor: '#22a000',
                    _hover: {
                      bg: '#1a8000',
                      borderColor: '#1a8000',
                    }
                  }
                }
              }}
            >
              Show my real name to all other users
            </Checkbox>
            {!showName && (
              <FormControl isRequired isInvalid={!!usernameError}>
                <FormLabel mb={1}>Username</FormLabel>
                <Input
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  onBlur={() => setTouched(t => ({...t, username: true}))}
                  autoComplete="nickname"
                />
                {usernameError && <FormErrorMessage>Username is required.</FormErrorMessage>}
              </FormControl>
            )}
            {/* Phone Number Section */}
            <Box w="100%">
              <FormControl isRequired isInvalid={!!phoneNumberError}>
                <FormLabel mb={1} color="gray.200" fontWeight="medium">Mobile phone number</FormLabel>
                <Input
                  value={formatPhoneNumber(phoneNumber)}
                  onChange={e => {
                    // Get the raw input value and normalize it to store only digits
                    const normalized = normalizePhoneNumber(e.target.value);
                    // Store the normalized value (only digits, max 10)
                    setPhoneNumber(normalized);
                  }}
                  onBlur={() => setTouched(t => ({...t, phoneNumber: true}))}
                  placeholder={formatPhoneNumber('1234567890')}
                  autoComplete="tel"
                  type="tel"
                  bg="gray.700"
                  borderColor="gray.500"
                  color="white"
                  _placeholder={{ color: 'gray.400' }}
                  _focus={{ borderColor: '#22a000', boxShadow: '0 0 0 1px #22a000' }}
                  maxLength={14} // (XXX) XXX-XXXX = 14 characters
                />
                <Text fontSize="sm" color="gray.300" mt={1}>
                  We'll text important account and product updates.
                </Text>
                {phoneNumberError && <FormErrorMessage>Phone number must be 10 digits.</FormErrorMessage>}
              </FormControl>

              {/* SMS Consent Section - Only show when valid phone number is entered */}
              {isValidPhoneNumber && (
                <Box 
                  mt={4} 
                  p={4} 
                  bg="gray.700" 
                  borderRadius="md" 
                  border="1px solid" 
                  borderColor="gray.500"
                >
                  <Text fontWeight="bold" color="gray.100" mb={2}>
                    Dominus Alerts (by Mobile Global Esports)
                  </Text>
                  <Text fontSize="sm" color="gray.300" mb={3}>
                    Get account and product updates by text. You can opt out anytime.
                  </Text>
                  
                  <Flex align="flex-start" gap={3}>
                    <Checkbox
                      isChecked={smsConsent}
                      onChange={e => setSmsConsent(e.target.checked)}
                      colorScheme="green"
                      mt={1}
                      sx={{
                        '& .chakra-checkbox__control': {
                          borderColor: '#22a000',
                          _checked: {
                            bg: '#22a000',
                            borderColor: '#22a000',
                            _hover: {
                              bg: '#1a8000',
                              borderColor: '#1a8000',
                            }
                          }
                        }
                      }}
                    />
                    <Box fontSize="15px" color="gray.200" lineHeight="1.4">
                      <Text>
                        I agree to receive <Text as="span" fontWeight="bold">recurring</Text> text messages from <Text as="span" fontWeight="bold">Dominus</Text> (by <Text as="span" fontWeight="bold">Mobile Global Esports</Text>) at the number provided about my account and product updates. <Text as="span" fontWeight="bold">Msg & data rates may apply. Msg frequency varies. Reply STOP to cancel, HELP for help.</Text>
                      </Text>
                    </Box>
                  </Flex>
                  
                  <Text fontSize="13px" color="gray.300" mt={3}>
                    By checking this box, you confirm you're the owner of the number provided and agree to our{' '}
                    <button
                      type="button"
                      style={{
                        color: '#22a000',
                        textDecoration: 'underline',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        fontSize: 'inherit',
                      }}
                      onClick={() => setSmsOpen(true)}
                    >
                      SMS Terms
                    </button>
                    {' '}and{' '}
                    <button
                      type="button"
                      style={{
                        color: '#22a000',
                        textDecoration: 'underline',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        fontSize: 'inherit',
                      }}
                      onClick={() => setPrivacyOpen(true)}
                    >
                      Privacy Policy
                    </button>
                    .
                  </Text>
                </Box>
              )}
            </Box>
            <Flex align="center">
              <Checkbox
                isChecked={accepted}
                onChange={e => setAccepted(e.target.checked)}
                colorScheme="green"
                size="lg"
                sx={{
                  '& .chakra-checkbox__control': {
                    borderColor: '#22a000',
                    _checked: {
                      bg: '#22a000',
                      borderColor: '#22a000',
                      _hover: {
                        bg: '#1a8000',
                        borderColor: '#1a8000',
                      }
                    }
                  }
                }}
              />
              <Box as="span" ml={3} fontSize="lg" maxW="420px">
                I accept the
                <button
                  type="button"
                  style={{
                    color: '#22a000',
                    textDecoration: 'underline',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    margin: '0 0.3em',
                    cursor: 'pointer',
                    fontSize: 'inherit',
                  }}
                  onClick={() => openTerms('terms-and-conditions.html', 'Terms & Conditions', { displayMode: 'fullscreen' })}
                >
                  terms and conditions
                </button>
                and agree to the
                <button
                  type="button"
                  style={{
                    color: '#22a000',
                    textDecoration: 'underline',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    margin: '0 0.3em',
                    cursor: 'pointer',
                    fontSize: 'inherit',
                  }}
                  onClick={() => setPrivacyOpen(true)}
                >
                  privacy policy
                </button>
              </Box>
            </Flex>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={handleSignOut}>
            Sign Out
          </Button>
          <Button
            colorScheme="blue"
            ml={3}
            onClick={handleAccept}
            isDisabled={!isFormValid || submitting}
            isLoading={submitting}
          >
            Continue
          </Button>
        </ModalFooter>
      </ModalContent>
      {termsOpen && (
        <Terms
          documentPath={documentPath}
          title={title}
          isOpen={termsOpen}
          onClose={closeTerms}
          displayMode={displayMode}
          showAcceptDecline={showAcceptDecline}
          acceptText={acceptText}
          declineText={declineText}
          onAccept={onAccept}
          onDecline={onDecline}
        />
      )}
      {privacyOpen && (
        <Terms
          documentPath="privacy-policy.html"
          title="Privacy Policy"
          isOpen={privacyOpen}
          onClose={() => setPrivacyOpen(false)}
          displayMode="fullscreen"
        />
      )}
      {smsOpen && (
        <Terms
          documentPath="sms-policy.html"
          title="SMS Policy"
          isOpen={smsOpen}
          onClose={() => setSmsOpen(false)}
          displayMode="fullscreen"
        />
      )}
    </Modal>
  );
};

export default AcceptTerms; 